/**
 * @fileoverview - Adds or updates package protection in cart.
 * Note: Does not support multiple currencies, only works well with
 * USD.
 */

/**
 * Adds free productY to cart if productX is in cart.
 * Note: Logic is currently set to add 1 free nail glue for every C-curve
 * product type purchase.
 */
async function buyXgetY() {
	const GLUE_VARIANT_ID = 39986663719048;

	// Get cart items from current cart.
	const cartContents = await fetch(
		window.theme.localeRootUrl + "/cart.js"
	).then((response) => response.json());

	let cCurvesInCart = 0;
	let glueInCart = 0;

	for (const item of cartContents.items) {
		// Check if a C-curve exists in cart.
		if (
			item.product_type === "C-Curve" &&
			!Object.keys(item.properties).length
		) {
			cCurvesInCart += item.quantity;
		}

		// Check if glue is already in cart.
		if (item.id === GLUE_VARIANT_ID && !Object.keys(item.properties).length) {
			glueInCart += item.quantity;
		}
	}

	const glueToAdd = cCurvesInCart - glueInCart;
	if (glueToAdd <= 0) {
		return;
	}

	const response = await fetch(window.theme.localeRootUrl + "/cart/add.js", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			items: [{ id: GLUE_VARIANT_ID, quantity: glueToAdd }],
		}),
	});

	if (!response.ok) {
		throw new Error("Failed to add free glue");
	}
}

/**
 * Adds or removes free gift item based on cart threshold.
 * Orders over $200 (excluding package protection) get a free item.
 * Ensures only ONE free gift exists in cart (prevents duplicates).
 * @returns {Promise} - Resolves when check is complete.
 */
// let _freeItemCheckerPromise = null;
//
// async function freeItemChecker() {
//   if (_freeItemCheckerPromise) {
//     await _freeItemCheckerPromise;
//   }
//   _freeItemCheckerPromise = _executeFreeItemCheck();
//   try {
//     await _freeItemCheckerPromise;
//   } finally {
//     _freeItemCheckerPromise = null;
//   }
// }
//
// async function _executeFreeItemCheck() {
//   const FREE_ITEM_VARIANT_ID = 44508344942728; // UPDATE: Set to current promo item
//   const THRESHOLD_CENTS = 20000; // $200.00
//
//   if (sessionStorage.getItem('removed_free_gift')) {
//     return;
//   }
//
//   const cart = await fetch(window.theme.localeRootUrl + '/cart.js').then(
//     (response) => response.json()
//   );
//
//   let cartTotal = cart.total_price;
//   const freeItems = [];
//
//   for (const item of cart.items) {
//     if (item.handle === 'kiara-sky-package-protection') {
//       cartTotal -= item.price * item.quantity;
//     }
//     if (
//       item.id === FREE_ITEM_VARIANT_ID &&
//       item.properties &&
//       item.properties._free_gift
//     ) {
//       freeItems.push({ key: item.key, quantity: item.quantity });
//       cartTotal -= item.price * item.quantity;
//     }
//   }
//
//   const qualifies = cartTotal >= THRESHOLD_CENTS;
//   const hasFreeItem = freeItems.length > 0;
//
//   if (qualifies && !hasFreeItem) {
//     await fetch(window.theme.localeRootUrl + '/cart/add.js', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify({
//         items: [
//           {
//             id: FREE_ITEM_VARIANT_ID,
//             quantity: 1,
//             properties: { _free_gift: 'spend-200-promo' },
//           },
//         ],
//       }),
//     });
//     return;
//   }
//
//   if (hasFreeItem) {
//     const updates = {};
//     let needsUpdate = false;
//
//     for (let i = 1; i < freeItems.length; i++) {
//       updates[freeItems[i].key] = 0;
//       needsUpdate = true;
//     }
//
//     const targetQty = qualifies ? 1 : 0;
//     if (freeItems[0].quantity !== targetQty) {
//       updates[freeItems[0].key] = targetQty;
//       needsUpdate = true;
//     }
//
//     if (needsUpdate) {
//       await fetch(window.theme.localeRootUrl + '/cart/update.js', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           'X-Requested-With': 'XMLHttpRequest',
//         },
//         body: JSON.stringify({ updates }),
//       });
//     }
//   }
// }

/**
 * Package protection variant tiers based on cart total.
 * Each tier has a variant ID and maximum cart value in cents.
 */
const INSURANCE_VARIANTS = [
	{ id: 39553534853256, maxCents: 14998 }, // $0.01 - $149.98
	{ id: 39553534886024, maxCents: 24998 }, // $149.99 - $249.98
	{ id: 39553534918792, maxCents: 34998 }, // $249.99 - $349.98
	{ id: 39553534951560, maxCents: 44998 }, // $349.99 - $449.98
	{ id: 39553534984328, maxCents: 60100 }, // $449.99 - $601.00
	{ id: 39553535017096, maxCents: Infinity }, // $601.01+
];

/**
 * Returns the correct insurance variant ID for a given cart total.
 * @param {number} cartTotalCents - Cart total in cents (excluding insurance).
 * @returns {number|null} - Variant ID or null if no insurance needed.
 */
function getCorrectInsuranceVariant(cartTotalCents) {
	if (cartTotalCents < 1) return null;
	return INSURANCE_VARIANTS.find((tier) => cartTotalCents <= tier.maxCents).id;
}

/**
 * Promise-based queue to prevent concurrent insurance checks.
 */
let _insuranceCheckerPromise = null;

/**
 * Adds or updates package protection in cart. Ensures only ONE package
 * protection item exists at the correct tier for the cart total.
 * Uses a queue mechanism to prevent race conditions from concurrent calls.
 * @returns {Promise} - Resolves when check is complete.
 */
async function insuranceChecker() {
	// Queue mechanism - if already running, wait for it then run again
	if (_insuranceCheckerPromise) {
		await _insuranceCheckerPromise;
	}

	_insuranceCheckerPromise = _executeInsuranceCheck();

	try {
		await _insuranceCheckerPromise;
	} finally {
		_insuranceCheckerPromise = null;
	}
}

/**
 * Executes the insurance check logic. Called by insuranceChecker() wrapper.
 */
async function _executeInsuranceCheck() {
	// Note: Uncomment to enable free gift for orders over $200
	// await freeItemChecker();

	// Note: Uncomment to enable buy X get Y logic
	// await buyXgetY();

	// Skip if user has opted out of insurance this session
	if (sessionStorage.getItem("removed_insurance")) {
		return;
	}

	// Fetch current cart
	const cart = await fetch(window.theme.localeRootUrl + "/cart.js").then(
		(response) => response.json()
	);

	// Collect ALL insurance items and calculate adjusted cart total
	const insuranceItems = [];
	let cartTotal = cart.total_price;

	for (const item of cart.items) {
		if (item.handle === "kiara-sky-package-protection") {
			insuranceItems.push({
				key: item.key,
				id: item.id,
				quantity: item.quantity,
			});
			// Exclude insurance price from cart total calculation
			cartTotal -= item.price * item.quantity;
		}
	}

	// Determine the correct variant for this cart total
	const correctVariantId = getCorrectInsuranceVariant(cartTotal);

	// Find if correct variant already exists with qty=1
	const correctItemIndex = insuranceItems.findIndex(
		(item) => item.id === correctVariantId && item.quantity === 1
	);

	// Build updates object for removing duplicates/wrong variants
	const updates = {};
	let needsUpdate = false;

	insuranceItems.forEach((item, index) => {
		if (index === correctItemIndex) {
			// Keep this one - it's the correct variant with correct quantity
			return;
		}
		// Remove all others (duplicates, wrong tiers, wrong quantities)
		updates[item.key] = 0;
		needsUpdate = true;
	});

	// Case 1: Need to add correct variant (doesn't exist yet)
	if (correctVariantId && correctItemIndex === -1) {
		// First remove any duplicates/wrong variants
		if (needsUpdate) {
			await fetch(window.theme.localeRootUrl + "/cart/update.js", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ updates }),
			});
		}
		// Then add the correct variant
		const addResponse = await fetch(
			window.theme.localeRootUrl + "/cart/add.js",
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					items: [{ id: correctVariantId, quantity: 1 }],
				}),
			}
		);

		// Publish custom event for tracking
		if (addResponse.ok) {
			const addedItems = await addResponse.json();
			const addedItem = addedItems.items?.[0] || addedItems;

			if (window.Shopify?.analytics?.publish) {
				Shopify.analytics.publish("diarmuids_custom_package_protection", {
					value: addedItem.price / 100,
					currency: "USD",
					items: [
						{
							item_id: String(correctVariantId),
							item_name: addedItem.title,
							price: addedItem.price / 100,
						},
					],
				});
			}
		}
		return;
	}

	// Case 2: Just need to remove duplicates/wrong variants
	if (needsUpdate) {
		await fetch(window.theme.localeRootUrl + "/cart/update.js", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ updates }),
		});
	}
}
